import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO component for managing all document head tags
 * @param {Object} props
 * @param {string} props.title - The page title
 * @param {string} props.description - The page description
 * @param {string} props.canonicalUrl - The canonical URL
 * @param {string} props.imageUrl - The URL for the og:image
 * @param {string} props.imageAlt - Alt text for the og:image
 * @param {Object} props.structuredData - JSON-LD structured data object
 */
const SEO = ({
  title,
  description,
  canonicalUrl,
  imageUrl,
  imageAlt = '',
  structuredData = null,
  children,
}) => {
  // Ensure canonicalUrl has a value
  const url = canonicalUrl || (typeof window !== 'undefined' ? window.location.href : '');
  // Ensure imageUrl has a value and is absolute
  const image = imageUrl || `${url.split('/').slice(0, 3).join('/')}/og-homepage.jpg`;

  return (
    <Helmet prioritizeSeoTags>
      {/* General tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph tags */}
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      <meta property="og:type" content="website" />
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD structured data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {children}
    </Helmet>
  );
};

export default SEO;
